/**
 * useStoryClipPublishRecords.ts
 *
 * The Content Studio clips grid's publish records, fetched once for the whole
 * story and indexed by Video record id. Backs the cards' published state
 * (PLAT-11591), which the legacy `videoFilePrimary` / `lastPublishJson` fields
 * cannot answer for a clip published only in a translation.
 *
 * One request, not one per card: the grid renders a story's clips unpaginated,
 * and `getRecordsForVideos` batches on `videoId: { in: … }`. Live updates are
 * overlaid from `PublishTrackingStore` the way `useClipPublishRecords` does, so
 * a dub that finishes rendering lights its badge without a refetch.
 *
 * Deliberately NOT memoized: MobX observable reads must happen in the render
 * path of an `observer` component to stay reactive (same reasoning as
 * `useClipPublishRecords`). The index is rebuilt per render over a small N.
 */

import { usePublishRecordService } from '@src/hooks/usePublishRecordService';
import {
  usePublishRecordsStore,
  usePublishTrackingStore,
} from '@src/stores-v2/StoreContext';
import { useEffect, useMemo } from 'react';

import type { PublishRecord } from '@src/types.ts/publishRecord';
import type { OtherVideo } from '@src/types.ts/story';

/** Records for one Video record id; `EMPTY` when the clip has none. */
export type ClipPublishRecordsLookup = (videoId: string) => PublishRecord[];

const EMPTY: PublishRecord[] = [];

/** Every per-AR Video record id behind the grid's clips (main + associated). */
function collectVariantIds(videos: OtherVideo[] | undefined): string[] {
  const ids = new Set<string>();
  for (const video of videos ?? []) {
    if (video.id) ids.add(video.id);
    for (const associated of video.associatedVideos ?? []) {
      if (associated.id) ids.add(associated.id);
    }
  }
  return Array.from(ids);
}

export function useStoryClipPublishRecords(
  storyId: string | undefined,
  videos: OtherVideo[] | undefined,
): ClipPublishRecordsLookup {
  const publishRecordService = usePublishRecordService();
  const publishRecordsStore = usePublishRecordsStore();
  const publishTrackingStore = usePublishTrackingStore();

  const variantIds = collectVariantIds(videos);
  // `videos` is a fresh array every render (the grid filters hidden clips
  // inline), so the effect keys off a primitive instead — a fresh-array dep
  // would re-fire the fetch on every render (pitfall 18).
  const variantIdsKey = variantIds.join(',');
  const variantIdsForFetch = useMemo(
    () => (variantIdsKey ? variantIdsKey.split(',') : []),
    [variantIdsKey],
  );

  const cached = storyId
    ? publishRecordsStore.getStoryRecords(storyId)
    : undefined;
  // A DEP, not a guard: `ensureForStory` must still run for ids added to an
  // existing entry (a variant created since), and it decides for itself what is
  // missing. What this adds is the re-fire after `clearStory`, which can land at
  // any point relative to this mount — without it, a clear under a mounted grid
  // leaves it on EMPTY with nothing to trigger a re-fetch.
  const needsFetch = cached === undefined;

  useEffect(() => {
    if (!storyId || variantIdsForFetch.length === 0) return;
    void publishRecordsStore.ensureForStory(
      storyId,
      variantIdsForFetch,
      publishRecordService,
    );
  }, [
    storyId,
    variantIdsForFetch,
    needsFetch,
    publishRecordsStore,
    publishRecordService,
  ]);

  const fetched = cached ?? EMPTY;

  // Merge fetched history with the tracking store's live entries, which win on
  // id conflict (fresher status). Records dispatched after the fetch appear
  // here rather than waiting for a re-open.
  const byId = new Map<string, PublishRecord>();
  for (const record of fetched) byId.set(record.id, record);
  if (storyId) {
    for (const [, record] of publishTrackingStore.records) {
      if (record.storyId === storyId) byId.set(record.id, record);
    }
  }

  const byVideoId = new Map<string, PublishRecord[]>();
  for (const record of byId.values()) {
    const bucket = byVideoId.get(record.videoId);
    if (bucket) bucket.push(record);
    else byVideoId.set(record.videoId, [record]);
  }
  // The selectors take newest-first (`pickNewestBestResolution` scopes to the
  // newest publish by reading `candidates[0]`), which the merge does not
  // preserve.
  for (const bucket of byVideoId.values()) {
    bucket.sort((a, b) => publishedTime(b) - publishedTime(a));
  }

  return (videoId: string) => byVideoId.get(videoId) ?? EMPTY;
}

function publishedTime(record: PublishRecord): number {
  return record.publishedAt ? new Date(record.publishedAt).getTime() : 0;
}
